// =============================================================
//  AnnouncementDetailScreen — Détail d'une annonce + actions admin
// =============================================================

import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../private/firebaseConfig';

import AdminLayout from './web-ui/AdminLayout';
import { Card, Button, Badge, Divider, Loading } from './web-ui/kit';
import { colors, spacing } from './web-ui/theme';

const fmtDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('fr-FR');
};

const statusVariant = (s) => {
  if (s === 'active') return 'success';
  if (s === 'pending') return 'warning';
  if (s === 'rejected') return 'danger';
  return 'neutral';
};

export default function AnnouncementDetailScreen({ route, navigation }) {
  const idParam = route?.params?.id;
  const initial = route?.params?.announcement || null;

  const [announcement, setAnnouncement] = useState(initial);
  const [loading, setLoading] = useState(!initial);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (initial || !idParam) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'announcements', idParam));
        if (snap.exists()) setAnnouncement({ id: snap.id, ...snap.data() });
      } catch (e) {
        console.log('announcement detail load error', e?.message || e);
      } finally {
        setLoading(false);
      }
    })();
  }, [idParam, initial]);

  const setStatus = async (newStatus) => {
    if (!announcement) return;
    setActionLoading(true);
    try {
      await updateDoc(doc(db, 'announcements', announcement.id), { status: newStatus });
      setAnnouncement((p) => ({ ...p, status: newStatus }));
    } catch (e) {
      console.log('update status error', e?.message || e);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Détail de l'annonce">
        <Loading label="Chargement..." />
      </AdminLayout>
    );
  }

  if (!announcement) {
    return (
      <AdminLayout title="Annonce introuvable">
        <Card>
          <Text style={{ color: colors.textSecondary, padding: spacing.md }}>
            Cette annonce n'existe plus ou est inaccessible.
          </Text>
        </Card>
      </AdminLayout>
    );
  }

  const title = announcement.title || announcement.titre || 'Annonce';

  return (
    <AdminLayout title={title} subtitle="Détail de l'annonce">
      <View style={styles.actions}>
        <Button variant="secondary" icon="arrow-left" onPress={() => navigation.goBack()}>Retour</Button>
        {announcement.status !== 'active' && (
          <Button
            variant="primary"
            icon="check"
            loading={actionLoading}
            onPress={() => setStatus('active')}
          >
            Approuver
          </Button>
        )}
        {announcement.status !== 'rejected' && (
          <Button
            variant="danger"
            icon="close"
            loading={actionLoading}
            onPress={() => setStatus('rejected')}
          >
            Rejeter
          </Button>
        )}
      </View>

      <Card>
        <View style={{ flexDirection: 'row', gap: 6, marginBottom: spacing.md, flexWrap: 'wrap' }}>
          <Badge variant={statusVariant(announcement.status)}>
            {announcement.status || '—'}
          </Badge>
          {!!announcement.type && <Badge variant="primary">{announcement.type}</Badge>}
        </View>

        <Text style={styles.title}>{title}</Text>
        {!!announcement.subtitle && <Text style={styles.subtitle}>{announcement.subtitle}</Text>}

        <Divider />

        {!!announcement.description && (
          <>
            <Text style={styles.sectionTitle}>Description</Text>
            <Text style={styles.body}>{announcement.description}</Text>
            <Divider />
          </>
        )}

        <View style={styles.grid}>
          <InfoItem label="Auteur" value={announcement.centerName || announcement.author} />
          <InfoItem label="Date" value={fmtDate(announcement.createdAt)} />
          <InfoItem label="Expire le" value={fmtDate(announcement.expiresAt)} />
          <InfoItem label="Vues" value={announcement.views || 0} />
        </View>
      </Card>
    </AdminLayout>
  );
}

function InfoItem({ label, value }) {
  return (
    <View style={styles.infoItem}>
      <Text style={styles.infoLabel}>{label}</Text>
      <Text style={styles.infoValue}>{value || '—'}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: spacing.sm, marginBottom: spacing.md, flexWrap: 'wrap' },
  title: { fontSize: 22, fontWeight: '700', color: colors.text, letterSpacing: -0.3 },
  subtitle: { fontSize: 14, color: colors.textSecondary, marginTop: 4 },
  sectionTitle: { fontSize: 14, fontWeight: '700', color: colors.text, marginBottom: spacing.sm },
  body: { fontSize: 14, color: colors.textSecondary, lineHeight: 21 },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.md },
  infoItem: {
    flex: 1,
    minWidth: 200,
    backgroundColor: colors.surfaceAlt,
    padding: spacing.md,
    borderRadius: 10,
  },
  infoLabel: { fontSize: 11, color: colors.textMuted, fontWeight: '600', textTransform: 'uppercase' },
  infoValue: { fontSize: 14, color: colors.text, fontWeight: '600', marginTop: 4 },
});
